/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  KPI_CARD_GROUPS,
  STATUS_COLOR,
  formatKgMetric,
  getMetric,
  sumMetrics,
} from './constants';

const TOTAL_KEY = 'TOTAL';

const KPI_ICONS = {
  KPI_EM_INSPECAO: <SearchIcon />,
  KPI_EM_DIGITACAO: <EditNoteOutlinedIcon />,
  KPI_FINALIZADAS: <CheckCircleOutlineIcon />,
};

const CARDS = [
  { key: TOTAL_KEY, label: 'Total', icon: <FormatListBulletedIcon /> },
  ...KPI_CARD_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    color: group.color,
    icon: KPI_ICONS[group.key] || <FormatListBulletedIcon />,
  })),
];

const ROW_DIVIDER_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  '& > *': {
    flex: 1,
    minWidth: { xs: '50%', sm: 160, md: 0 },
  },
  '& > *:not(:first-of-type)': {
    borderLeft: { sm: 1 },
    borderColor: 'divider',
  },
};

const resolveColor = (theme, colorKey) => {
  if (colorKey === 'default' || !theme.palette[colorKey]) {
    return theme.palette.text.secondary;
  }
  return theme.palette[colorKey].main;
};

const formatNumber = (n) => (n ?? 0).toLocaleString('pt-BR');

const KpiCell = ({ config, metric, active, onClick }) => {
  const colorKey =
    config.key === TOTAL_KEY ? STATUS_COLOR.TOTAL : config.color || STATUS_COLOR[config.key] || 'default';

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 2,
        py: 1.75,
        borderRadius: 0,
        transition: 'background-color 0.2s ease',
        backgroundColor: (theme) =>
          active ? `${resolveColor(theme, colorKey)}14` : 'transparent',
        '&:hover': {
          backgroundColor: (theme) => `${resolveColor(theme, colorKey)}0F`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => resolveColor(theme, colorKey),
            backgroundColor: (theme) => `${resolveColor(theme, colorKey)}1F`,
            border: (theme) =>
              active ? `2px solid ${resolveColor(theme, colorKey)}` : '2px solid transparent',
          }}
        >
          {React.cloneElement(config.icon, { sx: { fontSize: 20 } })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.8125rem' }}
            noWrap
            title={config.label}
          >
            {config.label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {formatNumber(metric.nsirs)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            NSIRs · {formatKgMetric(metric.totalKg)} kg
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
};

KpiCell.propTypes = {
  config: PropTypes.object.isRequired,
  metric: PropTypes.shape({ nsirs: PropTypes.number, totalKg: PropTypes.number }).isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

KpiCell.defaultProps = {
  active: false,
};

const KpiSkeleton = () => (
  <Box sx={{ px: 2, py: 1.75 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="70%" height={18} />
        <Skeleton width="40%" height={28} />
      </Box>
    </Stack>
  </Box>
);

const renderCard = (config, filtrado, activeStatus, onSelectStatus) => {
  const metric =
    config.key === TOTAL_KEY ? sumMetrics(filtrado) : getMetric(filtrado, config.key);
  const isTotal = config.key === TOTAL_KEY;
  const active = isTotal ? activeStatus == null : activeStatus === config.key;

  return (
    <KpiCell
      key={config.key}
      config={config}
      metric={metric}
      active={active}
      onClick={() => {
        if (isTotal) {
          onSelectStatus(null);
        } else {
          onSelectStatus(activeStatus === config.key ? null : config.key);
        }
      }}
    />
  );
};

const KpiStrip = ({ resumo, loading, activeStatus, onSelectStatus }) => {
  const { filtrado } = resumo;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={ROW_DIVIDER_SX}>
        {loading
          ? CARDS.map((config) => <KpiSkeleton key={config.key} />)
          : CARDS.map((config) => renderCard(config, filtrado, activeStatus, onSelectStatus))}
      </Box>
    </Paper>
  );
};

KpiStrip.propTypes = {
  resumo: PropTypes.shape({
    filtrado: PropTypes.object,
    total: PropTypes.object,
  }).isRequired,
  loading: PropTypes.bool,
  activeStatus: PropTypes.string,
  onSelectStatus: PropTypes.func.isRequired,
};

KpiStrip.defaultProps = {
  loading: false,
  activeStatus: null,
};

export default KpiStrip;
